/**
 * Configured axios instance with global error handling interceptors.
 * Automatically handles errors and formats them using errorUtils.
 */

import axios from "axios";
import { toast } from "sonner";
import { formatApiError } from "./errorUtils";

// Always use relative /api URLs. Works on any domain:
// - cbaiq.com → cbaiq.com/api
// - preview.emergentagent.com → preview.emergentagent.com/api
// - localhost → localhost/api (proxied to backend)
const BACKEND_URL = '';
export const API = '/api';

// Create axios instance
const apiClient = axios.create({
  baseURL: API,
  timeout: 90000, // 90 seconds default timeout (increased for LLM calls)
  headers: {
    "Content-Type": "application/json"
  }
});

// Response interceptor for global error handling
apiClient.interceptors.response.use(
  // Success handler
  (response) => {
    // For successful responses, just return the response
    return response;
  },
  // Error handler
  (error) => {
    // Handle different error scenarios
    
    // Network error (no response from server)
    if (!error.response) {
      const message = error.message === "Network Error" 
        ? "Network error. Please check your internet connection."
        : formatApiError(error, "Unable to connect to server");
      
      // Only show toast if not explicitly suppressed
      if (!error.config?.skipErrorToast) {
        toast.error(message);
      }
      return Promise.reject(error);
    }

    // HTTP error responses (4xx, 5xx)
    const status = error.response.status;
    
    // Handle 402 Payment Required - Credit/Plan issues
    if (status === 402) {
      const detail = error.response.data?.detail;
      
      if (detail && typeof detail === 'object') {
        if (detail.code === 'INSUFFICIENT_CREDITS') {
          // Trigger insufficient credits modal
          window.dispatchEvent(new CustomEvent('show-credit-modal', { detail }));
          return Promise.reject(error);
        } else if (detail.code === 'PLAN_REQUIRED') {
          // Trigger plan required modal
          window.dispatchEvent(new CustomEvent('show-plan-modal', { detail }));
          return Promise.reject(error);
        }
      }
      
      // Fallback for other 402 errors
      if (!error.config?.skipErrorToast) {
        toast.error(detail?.message || "Payment or subscription required");
      }
      return Promise.reject(error);
    }
    
    // Handle specific status codes
    if (status === 401) {
      // Unauthorized - token expired or invalid
      // Skip toast for auth endpoints to let login/register pages handle it
      const isAuthEndpoint = error.config.url?.includes('/auth/login') || 
                            error.config.url?.includes('/auth/register');
      
      if (!isAuthEndpoint && !error.config?.skipErrorToast) {
        toast.error("Session expired. Please login again.");
        
        // Clear token and redirect to login
        localStorage.removeItem("token");
        setTimeout(() => {
          window.location.href = "/login";
        }, 1500);
      }
    } else if (status === 403) {
      // Forbidden - check for email verification required
      const detail = error.response.data?.detail;
      
      if (detail && typeof detail === 'object' && detail.code === 'EMAIL_NOT_VERIFIED') {
        // Show verification required toast with resend option
        if (!error.config?.skipErrorToast) {
          toast.error('Please verify your email to use premium features', {
            duration: 5000,
            action: {
              label: 'Resend Email',
              onClick: async () => {
                try {
                  await apiClient.post('/auth/send-verification-email');
                  toast.success('Verification email sent!');
                } catch (e) {
                  toast.error('Failed to send email');
                }
              }
            }
          });
        }
      } else {
        if (!error.config?.skipErrorToast) {
          toast.error("You don't have permission to perform this action");
        }
      }
    } else if (status === 404) {
      // Not found
      const message = formatApiError(error, "Resource not found");
      if (!error.config?.skipErrorToast) {
        toast.error(message);
      }
    } else if (status === 422) {
      // Validation error (Pydantic)
      const message = formatApiError(error, "Validation error");
      if (!error.config?.skipErrorToast) {
        toast.error(message);
      }
    } else if (status === 429) {
      // Rate limit / daily cap
      const detail = error.response.data?.detail;
      const message = (detail && typeof detail === 'object' && detail.message)
        ? detail.message
        : (typeof detail === 'string' ? detail : "You've reached today's usage limit. It resets at midnight UTC.");
      if (!error.config?.skipErrorToast) {
        toast.error(message, { duration: 8000 });
      }
    } else if (status >= 500) {
      // Server error
      const message = formatApiError(error, "Server error. Please try again later.");
      if (!error.config?.skipErrorToast) {
        toast.error(message);
      }
    } else {
      // Other errors
      const message = formatApiError(error, "An error occurred. Please try again.");
      if (!error.config?.skipErrorToast) {
        toast.error(message);
      }
    }

    // Always reject the promise so calling code can handle it if needed
    return Promise.reject(error);
  }
);

// Request interceptor to add auth token automatically
apiClient.interceptors.request.use(
  (config) => {
    // Get token from localStorage
    const token = localStorage.getItem("token");
    
    // Add token to headers if it exists
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Helper to make API calls with optional success toast
 * @param {Function} apiCall - Async function that returns a promise
 * @param {string} successMessage - Optional success message to show
 * @param {string} errorMessage - Optional custom error message
 * @returns {Promise}
 */
export const apiWithToast = async (apiCall, successMessage = null, errorMessage = null) => {
  try {
    const response = await apiCall();
    if (successMessage) {
      toast.success(successMessage);
    }
    return response;
  } catch (error) {
    if (errorMessage) {
      toast.error(errorMessage);
    }
    throw error;
  }
};

export default apiClient;
